/**
 * Whether an error came from a browser extension rather than from RoleLogic.
 *
 * Extensions that inject scripts into the page's own world share its global
 * error handlers, so their failures arrive at window.onerror and
 * onunhandledrejection exactly like ours. One such extension rejecting a
 * promise every few seconds on /dashboard turned into a stream of error
 * reports and production alerts for code we neither ship nor can fix.
 *
 * The test is deliberately narrow: the stack must name at least one script,
 * and every script it names must be an extension's. An extension frame on top
 * of one of ours (an extension that wraps fetch, say) still reports, because
 * our call may be what broke it. A stack that names no script at all says
 * nothing about where it came from and reports too.
 *
 * One exception: a network failure raised inside an extension's fetch wrapper.
 * Its stack passes through our call, but a wrapper that chains its own `.then`
 * leaves a second promise to reject, and that one is unhandled however
 * carefully we catch ours. All it says is that the network dropped.
 *
 * The API's log-error endpoint applies the same test to reports from bundles
 * that predate this one (api/src/api/log-error/error-origin.ts); keep the two
 * in step.
 */

// Chromium (Chrome, Edge, Opera, Brave), Firefox, Safari, legacy Edge, and the
// placeholder Safari substitutes for extension script URLs it hides from pages.
const EXTENSION_SCRIPT =
  /^(?:chrome-extension|moz-extension|safari-extension|safari-web-extension|ms-browser-extension|webkit-masked-url):\/\//i;

// Script URLs in V8 ("at fn (url:1:2)"), Firefox and Safari ("fn@url:1:2")
// stacks. Parentheses end a URL so V8's wrapping is not swallowed into it.
const SCRIPT_URL = /[a-z][a-z0-9+.-]*:\/\/[^\s()]+/gi;

// How Chromium words a fetch() that got no response.
const NETWORK_FAILURE = /^TypeError: Failed to fetch\b/;

export const fromBrowserExtension = (stack: string | null | undefined) => {
  if (!stack) return false;
  const scripts = stack.match(SCRIPT_URL);
  if (!scripts) return false;
  if (NETWORK_FAILURE.test(stack) && EXTENSION_SCRIPT.test(scripts[0])) {
    return true;
  }
  return scripts.every((url) => EXTENSION_SCRIPT.test(url));
};
